import React, { useState } from 'react'
import moment from 'moment'
import {
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import LoadingSkeleton from 'components/LoadingSkeleton/LoadingSkeleton'
import { sortByKey } from 'utils/tableSort'
import useLoyaltyDiners from 'hooks/useLoyaltyDiners'
import { SectionTitle } from '../styles'
import {
  CardBox,
  CurrentPageInfo,
  EmptyStateText,
  EmptyStateWrapper,
  PaginationSelect,
  PaginationText,
  PaginationWrapper,
  RewardsHeader,
  RowsPerPageWrapper,
  StyledTableCell,
  StyledChip,
} from './styles'

type SortOrder = 'asc' | 'desc'
type SortKey =
  | 'name'
  | 'email'
  | 'phone_number'
  | 'points_remaining'
  | 'created_at'
  | 'last_ordered_at'

const LoyaltyDiners: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  // Sorting state
  const [sortBy, setSortBy] = useState<SortKey>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  // Fetch paginated diners
  const { loyaltyDiners, meta, loading } = useLoyaltyDiners(organizationId, currentPage, rowsPerPage)

  const handleSort = (columnId: SortKey) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(columnId)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }

  // Sort the current page’s diners client-side
  const sortedDiners = sortByKey(loyaltyDiners, `attributes.${sortBy}`, sortOrder)

  return (
    <CardBox>
      <RewardsHeader>
        <SectionTitle data-testid='diners-title'>Loyalty Program Members</SectionTitle>
      </RewardsHeader>
      {loading ? (
        <LoadingSkeleton lineCount={3} />
      ) : (
        <EmptyStateWrapper>
          {loyaltyDiners.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'name', label: 'Name' },
                      { id: 'email', label: 'Email' },
                      { id: 'phone_number', label: 'Phone Number' },
                      { id: 'points_remaining', label: 'Points Remaining' },
                      { id: 'created_at', label: 'Created Date' },
                      { id: 'last_ordered_at', label: 'Last Ordered At' },
                    ].map(({ id, label }) => (
                      <StyledTableCell key={id}>
                        <TableSortLabel
                          active={sortBy === id}
                          direction={sortBy === id ? sortOrder : 'asc'}
                          onClick={() => handleSort(id as SortKey)}
                        >
                          {label}
                        </TableSortLabel>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                {sortedDiners.map((diner) => (
                  <TableRow key={diner.id} style={{ borderBottom: '1px solid #d7d7d7' }}>
                    <TableCell data-testid={`reward-name-${diner.id}`}>
                      {diner.attributes.name}
                    </TableCell>
                    <TableCell data-testid={`reward-email-${diner.id}`}>
                      {diner.attributes.email || 'N/A'}
                    </TableCell>
                    <TableCell data-testid={`reward-phone-${diner.id}`}>
                      {diner.attributes.phone_number || 'N/A'}
                    </TableCell>
                    <TableCell data-testid={`reward-points-remaining-${diner.id}`}>
                      <StyledChip label={diner.attributes.cached_points ?? 0} />
                    </TableCell>
                    <TableCell data-testid={`reward-created-date-${diner.id}`}>
                      {diner.attributes.created_at
                        ? moment(diner.attributes.created_at).format('MM/DD/YYYY')
                        : 'N/A'}
                    </TableCell>
                    <TableCell data-testid={`reward-last-ordered-at-${diner.id}`}>
                      {diner.attributes.last_ordered_at
                        ? moment(diner.attributes.last_ordered_at).format('MM/DD/YYYY')
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyStateText>
              No diners have been created yet.
            </EmptyStateText>
          )}
        </EmptyStateWrapper>
      )}

      <PaginationWrapper>
        <CurrentPageInfo>
          {meta
            ? `${(meta.page - 1) * Number(meta.limit) + 1}-${Math.min(
                meta.page * Number(meta.limit),
                meta.total
              )} of ${meta.total}`
            : null}
        </CurrentPageInfo>

        <Pagination
          count={meta ? Math.ceil(meta.total / Number(meta.limit)) : 0}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          shape='rounded'
          color='primary'
          size='small'
        />

        <RowsPerPageWrapper>
          <PaginationText>Results per page:</PaginationText>
          <PaginationSelect
            value={rowsPerPage}
            onChange={(event) => {
              setCurrentPage(1) // Reset to first page
              setRowsPerPage(Number(event.target.value))
            }}
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </PaginationSelect>
        </RowsPerPageWrapper>
      </PaginationWrapper>
    </CardBox>
  )
}

export default LoyaltyDiners
